import React, {ReactElement, ReactNode} from "react";
import PortalDialogType from "../../../../dialog/PortalDialogType";
import MimeTypes from "../../../mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../mimetype/MimeTypeFilter";
import Parameter from "../../../../dialog/parameter/Parameter";
import PortalDocument from "../../../PortalDocument";
import PortalDocumentManager from "../../../PortalDocumentManager";
import DialogDefault from "../../../../dialog/parameter/PortalDialogDefault";
import AnnotationNoteDialogFields from "../../../../dialog/field/annotation/AnnotationNoteDialogFields";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Dropdown} from "primereact/dropdown";
import {ColorPicker} from "primereact/colorpicker";
import {InputNumber} from "primereact/inputnumber";
import validator from "validator";
import MimeType from "../../../mimetype/MimeType";
import Message from "../../../../../shared/log/Message";
import {MessageType} from "../../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Rectangle,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	TextAnnotation,
	TextAnnotationIcons,
	ToolboxAnnotationAnnotation,
	UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../../../dialog/PortalDialogSecurity";
import Translate from "../../../../../shared/translation/Translate";
import SidebarFunction, {SidebarFunctionProperties, SidebarFunctionState} from "../SidebarFunction";
import Immutable from "../../../../../shared/immutable/Immutable";
import {HelpIcon} from "../../../../../shared/annotation/icon/HelpIcon";
import {InsertIcon} from "../../../../../shared/annotation/icon/InsertIcon";
import {KeyIcon} from "../../../../../shared/annotation/icon/KeyIcon";
import {NewParagraphIcon} from "../../../../../shared/annotation/icon/NewParagraphIcon";
import {ParagraphIcon} from "../../../../../shared/annotation/icon/ParagraphIcon";
import {NoteIcon} from "../../../../../shared/annotation/icon/NoteIcon";
import {CheckIcon} from "../../../../../shared/annotation/icon/CheckIcon";
import {CircleIcon} from "../../../../../shared/annotation/icon/CircleIcon";
import {CrossIcon} from "../../../../../shared/annotation/icon/CrossIcon";
import {CrossHairIcon} from "../../../../../shared/annotation/icon/CrossHairIcon";
import {RightArrowIcon} from "../../../../../shared/annotation/icon/RightArrowIcon";
import {RightPointerIcon} from "../../../../../shared/annotation/icon/RightPointerIcon";
import {StarIcon} from "../../../../../shared/annotation/icon/StarIcon";
import {UpArrowIcon} from "../../../../../shared/annotation/icon/UpArrowIcon";
import {UpLeftArrowIcon} from "../../../../../shared/annotation/icon/UpLeftArrowIcon";
import {CommentIcon} from "../../../../../shared/annotation/icon/CommentIcon";
import * as ReactDOMServer from 'react-dom/server';
import {Base64} from "js-base64";
import FunctionHelper from "../../../../../shared/helper/FunctionHelper";

export const noteIconSize: number = 8;

export class AnnotationNoteFunctionDefinition extends SidebarFunction<ToolboxAnnotationAnnotation,
	SidebarFunctionProperties<ToolboxAnnotationAnnotation>, SidebarFunctionState<ToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AnnotationNoteDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_NOTE, signatureEditProfile, Intention.editingAnnotations, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["sidebar-function", "annotation-note-function"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				{this.props.visible &&
					<div className={classList.join(" ")}>
						{this.renderHeader()}
						{this.renderContents()}
						{this.renderFooter()}
					</div>
				}
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<AnnotationNoteDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add?.text)) {
			return null;
		}

		let annotationParameter: TextAnnotation = parameter!.add!.text![0];

		return (
			<>
				<div className="function-content">
					{this.renderError()}
					<fieldset className="p-col-12 p-grid">
						<legend>
							<Translate {...this.props} data="annotation.appearance.label" namespace="dialog"/>
						</legend>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.ICON)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-icon">
								<Translate {...this.props} data="annotation.appearance.icon.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Dropdown value={annotationParameter.icon} inputId="control-icon"
										  options={[
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.comment", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Comment
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.help", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Help
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.insert", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Insert
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.key", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Key
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.new_paragraph",
													  {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.NewParagraph
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.note", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Note
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.paragraph", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Paragraph
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.check", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Check
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.checkmark", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Checkmark
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.circle", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Circle
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.cross", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Cross
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.crosshair", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.CrossHair
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.right_arrow",
													  {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.RightArrow
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.right_pointer",
													  {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.RightPointer
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.star", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.Star
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.up_arrow", {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.UpArrow
											  },
											  {
												  label: Translate.value(
													  "annotation.appearance.icon.up_left_arrow",
													  {ns: "dialog"}
												  ),
												  value: TextAnnotationIcons.UpLeftArrow
											  },
										  ]}
										  onChange={(e) =>
											  this.onChange(AnnotationNoteDialogFields.ICON, e.value)
										  }
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.COLOR)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-color">
								<Translate {...this.props} data="annotation.appearance.color.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className={"p-inputgroup" + (
									errors.has(AnnotationNoteDialogFields.COLOR) ? " p-invalid" : ""
								)}>
									<InputText
										className={errors.has(AnnotationNoteDialogFields.COLOR) ? "p-invalid" : ""}
										tooltip={errors.has(AnnotationNoteDialogFields.COLOR) ? Translate.value(
											errors.get(AnnotationNoteDialogFields.COLOR)!, {ns: "message"}
										) : ""}
										value={FunctionHelper.applyValue(
											annotationParameter.color, TextAnnotation.getColorDefault()
										)}
										onInput={(e) => this.onChange(
											AnnotationNoteDialogFields.COLOR, e.currentTarget.value
										)}/>
									<ColorPicker className="p-inputgroup-addon" format="hex"
												 defaultColor={TextAnnotation.getColorDefault().replace(
													 "#", ""
												 )}
												 value={FunctionHelper.applyValue(
													 annotationParameter.color, TextAnnotation.getColorDefault()
												 ).replace("#", "")}
												 onChange={(e) => this.onChange(
													 AnnotationNoteDialogFields.COLOR, "#" + e.value
												 )} appendTo={document.getElementById("root")}
												 inputId="control-color"/>
								</div>
								{this.renderFieldError(errors.has(AnnotationNoteDialogFields.COLOR) ?
									Translate.value(
										errors.get(AnnotationNoteDialogFields.COLOR)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.OPACITY)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-opacity">
								<Translate {...this.props} data="annotation.appearance.opacity" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-opacity" step={1} suffix=" %" showButtons={true}
											 value={FunctionHelper.applyValue(
												 annotationParameter.opacity, TextAnnotation.getOpacityDefault()
											 )}
											 min={TextAnnotation.getOpacityMin()} useGrouping={false}
											 max={TextAnnotation.getOpacityMax()}
											 onChange={(e) => this.onChange(
												 AnnotationNoteDialogFields.OPACITY, e.value
											 )}/>
							</div>
						</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid">
						<legend>
							<Translate {...this.props} data="annotation.properties.label" namespace="dialog"/>
						</legend>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.CREATOR)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-creator">
								<Translate {...this.props} data="annotation.properties.creator.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-creator"
										   className={
											   errors.has(AnnotationNoteDialogFields.CREATOR) ? "p-invalid" : ""
										   }
										   tooltip={errors.has(AnnotationNoteDialogFields.CREATOR) ? Translate.value(
											   errors.get(AnnotationNoteDialogFields.CREATOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   annotationParameter.creator, TextAnnotation.getCreatorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationNoteDialogFields.CREATOR, e.currentTarget.value
										   )}/>
								{this.renderFieldError(errors.has(AnnotationNoteDialogFields.CREATOR) ?
									Translate.value(
										errors.get(AnnotationNoteDialogFields.CREATOR)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.SUBJECT)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-subject">
								<Translate {...this.props} data="annotation.properties.subject.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-subject"
										   value={FunctionHelper.applyValue(
											   annotationParameter.subject, TextAnnotation.getSubjectDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationNoteDialogFields.SUBJECT, e.currentTarget.value
										   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.CONTENTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-contents">
								<Translate {...this.props} data="annotation.properties.content.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputTextarea rows={3} id="control-contents"
											   value={FunctionHelper.applyValue(
												   annotationParameter.contents,
												   TextAnnotation.getContentsDefault()
											   )}
											   onInput={(e) => this.onChange(
												   AnnotationNoteDialogFields.CONTENTS, e.currentTarget.value
											   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.INTENTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-intents">
								<Translate {...this.props} data="annotation.properties.intents"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-intents"
										   value={FunctionHelper.applyValue(
											   annotationParameter.intents, TextAnnotation.getIntentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationNoteDialogFields.INTENTS, e.currentTarget.value
										   )}/>
							</div>
						</div>
					</fieldset>
				</div>
			</>
		);
	}

	protected onChange(field: AnnotationNoteDialogFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: TextAnnotation = parameter.add!.text![0];
		let errors: Map<AnnotationNoteDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case AnnotationNoteDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case AnnotationNoteDialogFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case AnnotationNoteDialogFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case AnnotationNoteDialogFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case AnnotationNoteDialogFields.ICON:
				annotationParameter.icon = value;
				break;
			case AnnotationNoteDialogFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case AnnotationNoteDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = TextAnnotation.getOpacityDefault();
				}

				annotationParameter.opacity = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});

		this.props.context.getPage().setState({
			mouseFunctionAnnotationParameter: Immutable.from(this.state.parameter?.data.toJson()),
		});
	}

	protected renderHeader(): ReactNode {
		return null;
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add?.text)) {
			return;
		}

		let annotationParameter: TextAnnotation = parameter!.add!.text![0];

		if (annotationParameter.creator === "") {
			let user: UserCredentials = this.props.context.session.getUser()!;

			annotationParameter.creator = FunctionHelper.applyStringValue(
				user.userName, Translate.value("annotation.properties.creator.default", {ns: "dialog"})
			);
		}

		if (annotationParameter.subject === "") {
			annotationParameter.subject =
				Translate.value("annotation.properties.subject.default.note", {ns: "dialog"});
		}

		if (annotationParameter.contents === "") {
			annotationParameter.contents =
				Translate.value("annotation.properties.content.default", {ns: "dialog"});
		}

		this.setState({
			parameter: this.state.parameter
		});
	};

	private getIconAppearance(): string {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let annotationParameter: TextAnnotation = parameter!.add!.text![0];
		let opacity: number = FunctionHelper.applyValue(
			annotationParameter.opacity, TextAnnotation.getOpacityDefault()
		);
		let width: number = FunctionHelper.applyValue(
			annotationParameter.position?.width, Rectangle.getWidthDefault()
		);
		let height: number = FunctionHelper.applyValue(
			annotationParameter.position?.height, Rectangle.getHeightDefault()
		);
		let color: string = FunctionHelper.applyValue(
			annotationParameter.color, TextAnnotation.getColorDefault()
		);

		let appearance: ReactElement;
		switch (annotationParameter.icon) {
			case TextAnnotationIcons.Help:
				appearance = <HelpIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Insert:
				appearance =
					<InsertIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Key:
				appearance = <KeyIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.NewParagraph:
				appearance =
					<NewParagraphIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Paragraph:
				appearance =
					<ParagraphIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Note:
				appearance = <NoteIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Check:
				appearance = <CheckIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Checkmark:
				appearance = <CheckIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Circle:
				appearance =
					<CircleIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Cross:
				appearance = <CrossIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.CrossHair:
				appearance =
					<CrossHairIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.RightArrow:
				appearance =
					<RightArrowIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.RightPointer:
				appearance =
					<RightPointerIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Star:
				appearance = <StarIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.UpArrow:
				appearance =
					<UpArrowIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.UpLeftArrow:
				appearance =
					<UpLeftArrowIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			default:
				appearance =
					<CommentIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
		}

		return ReactDOMServer.renderToStaticMarkup(appearance);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: TextAnnotation = parameter!.add!.text![0];
		annotationParameter.normalAppearance!.svg!.data.value = Base64.toBase64(this.getIconAppearance());

		let operationData: Array<BaseToolbox> = [
			BaseToolbox.fromJson({
				annotation: parameter.toJson()
			})
		];

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.note", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_NOTE;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const AnnotationNoteFunction = React.memo(AnnotationNoteFunctionDefinition,
	(
		prevProps: Readonly<SidebarFunctionProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<SidebarFunctionProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationNoteFunction;